/**
 * The keyboard, on screen — and the same list, runnable.
 *
 * GENERATED FROM THE KEYMAP, NEVER WRITTEN OUT. A hand-kept cheat sheet is a
 * second statement of what the keys are, and the moment somebody adds a binding
 * it starts lying about a client the reader cannot otherwise inspect. So every
 * row here comes from the binding array the RESOLVER is holding — `keys`,
 * `label`, `tier` and `capability` — which is also what makes the sheet honest
 * about a remapping: `interaction.md` asks for a cheat sheet that is "live,
 * reflects remaps", and it is, because the array it draws is the one that has
 * already been through `applyOverrides`.
 *
 * IT RUNS THEM TOO, and that is why `?` and `Mod+k` both open it. Every command
 * this client has is a bound command, so a runnable list of the bindings is
 * exactly what a command palette is; two surfaces would be this list rendered
 * twice, and the second one would be the one that goes stale. What is NOT here
 * from `interaction.md` §7 is the teaching hint after a run ("next time press
 * E") and the fuzzy ranking — both want a per-command counter in a local store
 * this client does not have yet.
 *
 * THE PANEL IS THE `.modal` SHAPE, not the settings drawer: this is a thing you
 * open, read and dismiss, over the list rather than beside it. Escape is bound
 * in CAPTURE for the reason `Confirm.tsx` gives — the shell has its own Escape
 * ladder and exactly one of the two may spend the press.
 */
import { useEffect, useId, useMemo, useRef, useState } from 'react'
import { blockedCapability, findConflicts, type Binding } from './model/keymap.ts'

export interface HelpProps {
  readonly open: boolean
  readonly onClose: () => void
  /**
   * The bindings the RESOLVER holds, remaps included — never `DEFAULT_BINDINGS`.
   *
   * Passed in rather than imported here, so that a sheet showing `y` for Archive
   * and a keyboard that still archives on `e` cannot happen: there is one array,
   * built once in the shell.
   */
  readonly bindings: readonly Binding[]
  /** From `/whoami`. ABSENT MEANS NOT KNOWN YET and gates nothing. */
  readonly capabilities?: ReadonlySet<string>
  /** Run one command. The sheet closes first; see `onRun` at the call site. */
  readonly onRun: (id: string) => void
  /**
   * What a command would act on, in the reader's words, or null.
   *
   * `interaction.md` §7 calls for the target to be SNAPSHOTTED when the palette
   * opens and shown as a chip, so that a change-feed patch underneath cannot
   * change what runs. Here the shell resolves it and this only draws it — the
   * snapshot is real because the sheet suppresses the shell's keyboard while it
   * is open, so nothing the reader does can move the cursor meanwhile.
   */
  readonly target: string | null
  /** Chords the stored remapping named a command for that does not exist. */
  readonly ignoredOverrides: readonly string[]
}

/**
 * A tier's heading.
 *
 * THE GROUPS COME FROM THE BINDINGS, NOT FROM THIS TABLE. A tier added to the
 * model and not to this record still gets a section — named by the tier itself
 * — rather than every binding in it silently vanishing from the one surface
 * that exists to say what the keyboard can do.
 */
const TIER_TITLES: Readonly<Record<string, string>> = {
  action: 'Actions',
  goto: 'Go to',
  select: 'Select',
}

/**
 * `Mod`, as the key this reader actually presses.
 *
 * The map spells it `Mod` because one binding has to serve both platforms —
 * `describeKey` folds Ctrl and Meta into it — but nobody has a Mod key, and a
 * cheat sheet that names a key nobody can find is not a cheat sheet.
 */
function keyLabel(part: string): string {
  if (part !== 'Mod') return part
  const ua = typeof navigator === 'undefined' ? '' : navigator.userAgent
  return /Mac|iPhone|iPad|iPod/.test(ua) ? '⌘' : 'Ctrl'
}

/**
 * One chord, as keycaps.
 *
 * `Binding.keys` is already the canonical spelling `describeKey` produces from a
 * real event — that agreement is what makes the resolver and this sheet talk
 * about the same key — so nothing is re-derived here. A SPACE separates the
 * steps of a chord (`g i`); a `+` separates the parts of one keystroke
 * (`Mod+Shift+k`).
 */
function Chord({ keys }: { keys: string }): React.JSX.Element {
  return (
    <span className="help-chord">
      {keys.split(' ').map((step, stepAt) => (
        <span className="help-step" key={`${step}-${stepAt}`}>
          {step.split('+').map((part, partAt) => (
            <kbd className="help-key" key={`${part}-${partAt}`}>
              {keyLabel(part)}
            </kbd>
          ))}
        </span>
      ))}
    </span>
  )
}

export function Help({
  open,
  onClose,
  bindings,
  capabilities,
  onRun,
  target,
  ignoredOverrides,
}: HelpProps): React.JSX.Element | null {
  const ids = useId()
  const [filter, setFilter] = useState('')
  const panelRef = useRef<HTMLDivElement>(null)
  const filterRef = useRef<HTMLInputElement>(null)
  const returnTo = useRef<HTMLElement | null>(null)

  // A CLOSED SHEET REMEMBERS NOTHING. Reopening it on the last search would
  // show four rows of the forty there are, and the reader who opened it to find
  // out what the keyboard can do would conclude that it can do four things.
  useEffect(() => {
    if (!open) setFilter('')
  }, [open])

  useEffect(() => {
    if (!open) return
    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== 'Escape') return
      event.preventDefault()
      // CAPTURE PLUS `stopPropagation`, exactly as `Confirm.tsx` does it: the
      // shell's Escape ladder is listening on the same window, and one press
      // must spend one rung — closing this sheet AND clearing the selection
      // underneath it is two things the reader asked for once.
      event.stopPropagation()
      onClose()
    }
    window.addEventListener('keydown', onKey, true)
    return () => window.removeEventListener('keydown', onKey, true)
  }, [open, onClose])

  /**
   * Focus goes to the filter on open and back out on close.
   *
   * The opener is captured only if it is OUTSIDE the panel — `Settings.tsx`
   * records why that condition is load-bearing under StrictMode — and it is
   * returned to only if it is still in the document, because the button that
   * opened this may well have been a row action on a message that has since
   * been archived from underneath it.
   */
  useEffect(() => {
    if (!open) return
    const opener = document.activeElement
    if (opener instanceof HTMLElement && !panelRef.current?.contains(opener)) returnTo.current = opener
    filterRef.current?.focus()
    return () => {
      const back = returnTo.current
      if (back?.isConnected) back.focus()
    }
  }, [open])

  const needle = filter.trim().toLowerCase()
  const shown = useMemo(
    () =>
      needle === ''
        ? bindings
        : // THE KEY IS SEARCHABLE AS WELL AS THE LABEL, because half the reason
          // to open this is "what does `[` do" — which is a question about a
          // key, and a filter over labels alone cannot answer it.
          bindings.filter(
            (binding) =>
              binding.label.toLowerCase().includes(needle) || binding.keys.toLowerCase().includes(needle),
          ),
    [bindings, needle],
  )

  const groups = useMemo(() => {
    const out = new Map<string, Binding[]>()
    for (const binding of shown) out.set(binding.tier, [...(out.get(binding.tier) ?? []), binding])
    return [...out.entries()]
  }, [shown])

  /**
   * Two commands on one key, in the reader's own map.
   *
   * `findConflicts` has existed and been tested since before there was a
   * client, with no caller anywhere — and a remapping mechanism with no editor
   * needs it more than one with an editor would, because the conflict arrives
   * from a hand-edited settings document rather than from a form that could
   * have refused it. The resolver takes the FIRST binding that matches, so an
   * unreported conflict is a command that silently stopped working.
   */
  const conflicts = useMemo(() => findConflicts(bindings), [bindings])
  const nameOf = useMemo(() => new Map(bindings.map((binding) => [binding.id, binding.label])), [bindings])

  if (!open) return null

  const run = (id: string): void => {
    // CLOSED FIRST, THEN RUN. The shell's keyboard is suppressed while this is
    // open, and `search` — one of the commands on this list — puts the caret in
    // a field that is behind the scrim until this returns null.
    onClose()
    onRun(id)
  }

  const first = shown.find((binding) => blockedCapability(binding, capabilities) === null)

  return (
    <div
      className="modal-scrim"
      onClick={(event) => {
        if (event.target === event.currentTarget) onClose()
      }}
    >
      <div
        className="help-panel"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${ids}-title`}
        tabIndex={-1}
        ref={panelRef}
      >
        <header className="help-head">
          <h2 className="help-title" id={`${ids}-title`}>
            Keyboard
          </h2>
          {/* WHAT IT WOULD ACT ON, before anybody presses anything. A palette
              that runs Archive without saying what it is about to archive is a
              button with a hidden argument. */}
          {target ? <span className="help-target">{target}</span> : null}
          <button type="button" className="help-close" aria-label="Close keyboard help" onClick={onClose}>
            &#215;
          </button>
        </header>

        <form
          className="help-filter"
          onSubmit={(event) => {
            event.preventDefault()
            // Enter runs the first command the filter left standing, which is
            // the whole of the palette's speed. A blocked one is skipped rather
            // than run and refused.
            if (first) run(first.id)
          }}
        >
          <input
            ref={filterRef}
            className="help-search"
            value={filter}
            onChange={(event) => setFilter(event.target.value)}
            placeholder="Filter commands"
            aria-label="Filter commands"
            spellCheck={false}
          />
        </form>

        {conflicts.length > 0 ? (
          <p className="help-warning" role="status">
            {conflicts.map((conflict) => (
              <span key={`${conflict.keys} ${conflict.context}`}>
                {conflict.keys} runs two commands in the {conflict.context}:{' '}
                {conflict.ids.map((id) => nameOf.get(id) ?? id).join(' and ')}. The first one wins.
              </span>
            ))}
          </p>
        ) : null}

        {ignoredOverrides.length > 0 ? (
          <p className="help-warning" role="status">
            Your settings remap {ignoredOverrides.join(', ')} to a command this version does not have, so those
            keys are unchanged.
          </p>
        ) : null}

        <div className="help-groups">
          {groups.map(([tier, group]) => (
            <section className="help-group" key={tier}>
              <h3 className="help-group-title">{TIER_TITLES[tier] ?? tier}</h3>
              <ul className="help-list">
                {group.map((binding) => {
                  const missing = blockedCapability(binding, capabilities)
                  return (
                    <li key={binding.id}>
                      <button
                        type="button"
                        className="help-command"
                        // THE ID, ON THE ELEMENT. It is what a test can iterate
                        // the keymap against, so a binding added without a place
                        // on this sheet fails rather than quietly not appearing.
                        data-command={binding.id}
                        disabled={missing !== null}
                        title={missing === null ? undefined : `Your account does not have ${missing}`}
                        onClick={() => run(binding.id)}
                      >
                        <span className="help-label">{binding.label}</span>
                        {missing === null ? null : <span className="help-blocked">needs {missing}</span>}
                        <Chord keys={binding.keys} />
                      </button>
                    </li>
                  )
                })}
              </ul>
            </section>
          ))}
          {shown.length === 0 ? <p className="help-empty">No command matches that.</p> : null}
        </div>
      </div>
    </div>
  )
}
